import React from 'react';
import { motion } from 'framer-motion';

interface StatsOverviewProps {
    stats: {
        totalUsers: number;
        activeUsers: number;
        totalLeads: number;
        totalProspectedLeads: number;
        totalSearches: number;
        totalTokens: number;
        totalCost: number;
    };
}

interface StatCardProps {
    icon: string;
    label: string;
    value: string | number;
    color: string;
    index: number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
        >
            <div className="flex items-center justify-between mb-3">
                <span className={`material-symbols-outlined text-2xl sm:text-3xl ${color}`}>
                    {icon}
                </span>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-text-dark dark:text-white mb-1">
                {value}
            </p>
            <p className="text-xs sm:text-sm text-text-dark/70 dark:text-gray-400">
                {label}
            </p>
        </motion.div>
    );
};

const StatsOverview: React.FC<StatsOverviewProps> = ({ stats }) => {
    const statCards = [
        {
            icon: 'group',
            label: 'Total de Usuários',
            value: stats.totalUsers,
            color: 'text-blue-500',
        },
        {
            icon: 'verified_user',
            label: 'Usuários Ativos',
            value: stats.activeUsers,
            color: 'text-green-500',
        },
        {
            icon: 'contact_page',
            label: 'Total de Leads',
            value: stats.totalLeads.toLocaleString(),
            color: 'text-purple-500',
        },
        {
            icon: 'check_circle',
            label: 'Leads Prospectados',
            value: stats.totalProspectedLeads.toLocaleString(),
            color: 'text-teal-500',
        },
        {
            icon: 'search',
            label: 'Total de Pesquisas',
            value: stats.totalSearches.toLocaleString(),
            color: 'text-orange-500',
        },
        {
            icon: 'token',
            label: 'Tokens Usados',
            value: stats.totalTokens.toLocaleString(),
            color: 'text-pink-500',
        },
        {
            icon: 'payments',
            label: 'Custo Total',
            value: `$${stats.totalCost.toFixed(2)}`,
            color: 'text-red-500',
        },
    ];

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {statCards.map((card, index) => (
                <StatCard key={card.label} {...card} index={index} />
            ))}
        </div>
    );
};

export default StatsOverview;
